import React, { createContext, useCallback, useContext, useState } from 'react';
import * as DomicilappService from '../services/DomicilappService';
import { DeliveryCompany } from '../types/fleet';

// Empresa de domicilios activa para la sección "Mi flota" (equivalente a StoreContext para tiendas)
interface FleetContextType {
    companies: DeliveryCompany[];
    companiesLoaded: boolean;
    company: DeliveryCompany | null;
    setCompanyId: (id: number) => void;
    refreshCompanies: () => Promise<void>;
}

const FleetContext = createContext<FleetContextType | undefined>(undefined);

const STORAGE_KEY = 'admin_selected_delivery_company_id';

export const FleetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [companies, setCompanies] = useState<DeliveryCompany[]>([]);
    const [companiesLoaded, setCompaniesLoaded] = useState(false);
    const [companyId, setCompanyIdState] = useState<number | null>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved ? Number(saved) : null;
    });

    const setCompanyId = (id: number) => {
        localStorage.setItem(STORAGE_KEY, String(id));
        setCompanyIdState(id);
    };

    const refreshCompanies = useCallback(async () => {
        try {
            const response = await DomicilappService.getMyDeliveryCompanies();
            const items = response?.data ?? [];
            setCompanies(items);
            setCompanyIdState((current) => {
                const selected = items.find((c) => c.id === current) ?? items[0];
                if (selected) localStorage.setItem(STORAGE_KEY, String(selected.id));
                return selected ? selected.id : null;
            });
        } catch {
            setCompanies([]);
        } finally {
            setCompaniesLoaded(true);
        }
    }, []);

    const company = companies.find((c) => c.id === companyId) ?? null;

    return (
        <FleetContext.Provider value={{ companies, companiesLoaded, company, setCompanyId, refreshCompanies }}>
            {children}
        </FleetContext.Provider>
    );
};

export const useFleet = (): FleetContextType => {
    const context = useContext(FleetContext);
    if (!context) {
        throw new Error('useFleet debe ser usado dentro de un <FleetProvider>');
    }
    return context;
};
